import React, { useEffect, useState } from 'react';
import useApi from '../../hooks/useApi';
import { v4 as uuidv4 } from 'uuid';

/*
 Props:
  - purchaseOrder / record / data (optional) : existing PO to edit
  - onClose
  - onSaveSuccess
*/
const PurchaseOrderForm = (props) => {
  const {
    purchaseOrder = null,
    record = null,
    data = null,
    onClose = () => {},
    onSaveSuccess = () => {}
  } = props;

  // Robust source detection: accept many shapes (record, record.data, wrapped props)
  const getSource = () => {
    let s = purchaseOrder || record || data || null;
    // unwrap common wrappers
    if (s && typeof s === 'object') {
      if (s.data && typeof s.data === 'object') s = s.data;
      else if (s.record && typeof s.record === 'object') s = s.record;
      else if (s._doc && typeof s._doc === 'object') s = s._doc;
    }
    // try to detect a candidate prop on entire props object (fallback)
    if (!s) {
      for (const v of Object.values(props)) {
        if (!v || typeof v !== 'object') continue;
        const candidate = v.data || v.record || v._doc || v;
        if (candidate && (candidate.supplier || candidate.location || Array.isArray(candidate.items) || candidate.expectedDeliveryDate)) {
          s = candidate;
          break;
        }
      }
    }
    return s || null;
  };

  const source = getSource();

  const api = useApi();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);

  const emptyForm = {
    supplier: '',
    locationType: 'store',
    locationId: '',
    expectedDeliveryDate: '',
    notes: '',
    items: [{ id: uuidv4(), product: '', quantityOrdered: '', costPrice: '', taxRate: '' }],
    status: 'Draft'
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [supRes, storesRes, whRes, prodRes] = await Promise.all([
          api.get('productAdditionalInfo/suppliers'),
          api.get('stores', { queryParams: { limit: 1000 } }),
          api.get('warehouses', { queryParams: { limit: 1000 } }),
          api.get('products', { queryParams: { limit: 1000 } })
        ]);
        setSuppliers(supRes?.data || supRes || []);
        setStores(storesRes?.data || storesRes || []);
        setWarehouses(whRes?.data || whRes || []);
        setProducts(prodRes?.data || prodRes || []);
      } catch (err) {
        console.error('Failed loading PO dropdowns', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  // populate when editing — re-run if props change or dropdowns load
  useEffect(() => {
    const src = getSource();
    if (!src) {
      setFormData(emptyForm);
      return;
    }
    setFormData({
      supplier: src.supplier?._id || src.supplier || src.supplierId || '',
      locationType: src.location?.type || src.locationType || 'store',
      locationId: src.location?.id || src.locationId || src.locationId || '',
      expectedDeliveryDate: src.expectedDeliveryDate ? new Date(src.expectedDeliveryDate).toISOString().slice(0,10) : (src.expectedDelivery ? new Date(src.expectedDelivery).toISOString().slice(0,10) : ''),
      notes: src.notes || src.note || '',
      items: Array.isArray(src.items) && src.items.length > 0
        ? src.items.map(i => ({
            id: uuidv4(),
            product: (typeof i.product === 'string' ? i.product : (i.product?._id || i.product?.id || i.productId || '')),
            quantityOrdered: i.quantityOrdered ?? i.quantity ?? i.qty ?? '',
            costPrice: i.costPrice ?? i.cost ?? '',
            taxRate: i.taxRate ?? i.tax ?? ''
          }))
        : [{ id: uuidv4(), product: '', quantityOrdered: '', costPrice: '', taxRate: '' }],
      status: src.status || 'Draft'
    });
    // re-run when props change
  }, [props, suppliers.length, stores.length, warehouses.length, products.length]);

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { id: uuidv4(), product: '', quantityOrdered: '', costPrice: '', taxRate: '' }] }));
  const removeItem = (id) => setFormData(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));
  const updateItem = (id, field, value) => setFormData(prev => ({ ...prev, items: prev.items.map(i => i.id === id ? { ...i, [field]: value } : i) }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    setError(null);
    if (!formData.supplier) return 'Select supplier';
    if (!formData.locationId) return 'Select location';
    const itemsValid = formData.items.filter(i => i.product && Number(i.quantityOrdered) > 0);
    if (itemsValid.length === 0) return 'Add at least one item with positive quantity';
    return null;
  };

  const handleSave = async (e) => {
    e && e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      supplier: formData.supplier,
      location: { type: formData.locationType, id: formData.locationId },
      items: formData.items.filter(i => i.product && Number(i.quantityOrdered) > 0).map(i => ({
        product: i.product,
        quantityOrdered: Number(i.quantityOrdered),
        costPrice: i.costPrice !== '' ? Number(i.costPrice) : undefined,
        taxRate: i.taxRate !== '' ? Number(i.taxRate) : undefined
      })),
      expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
      notes: formData.notes || undefined,
      status: formData.status || 'Draft'
    };

    try {
      if (source && (source._id || source.id)) {
        await api.put(`purchase-orders/${source._id || source.id}`, payload);
      } else {
        await api.post('purchase-orders', payload);
      }
      onSaveSuccess && onSaveSuccess();
      onClose && onClose();
    } catch (err) {
      console.error('PO save failed', err);
      setError(err?.message || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="p-4 max-w-3xl">
      {loading && <div className="text-sm text-gray-500">Loading...</div>}
      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Supplier</label>
          <select className="mt-1 block w-full border rounded p-2" name="supplier" value={formData.supplier} onChange={handleChange}>
            <option value="">Select supplier</option>
            {suppliers.map(s => <option key={s._id || s.id} value={s._id || s.id}>{s.name || s.companyName || (s._id || s.id)}</option>)}
          </select>
        </div>

        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Location Type</label>
          <select className="mt-1 block w-full border rounded p-2" name="locationType" value={formData.locationType} onChange={handleChange}>
            <option value="store">Store</option>
            <option value="warehouse">Warehouse</option>
          </select>

          <label className="text-sm font-medium mt-3">Location</label>
          <select className="mt-1 block w-full border rounded p-2" name="locationId" value={formData.locationId} onChange={handleChange}>
            <option value="">Select</option>
            {(formData.locationType === 'store' ? stores : warehouses).map(l => <option key={l._id || l.id} value={l._id || l.id}>{l.name || l.storeName || l.code || (l._id || l.id)}</option>)}
          </select>
        </div>
      </div>

      <div className="p-3 border rounded bg-white mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Items</div>
          <button type="button" onClick={addItem} className="px-2 py-1 bg-green-600 text-white rounded text-sm">+ Add Item</button>
        </div>

        <div className="space-y-2">
          {formData.items.map(it => (
            <div key={it.id} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-6">
                <select className="w-full border rounded p-2" value={it.product} onChange={(e) => updateItem(it.id, 'product', e.target.value)}>
                  <option value="">Select product</option>
                  {products.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name || p.sku || (p._id || p.id)}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <input className="w-full border rounded p-2" type="number" min="0" value={it.quantityOrdered} onChange={(e) => updateItem(it.id, 'quantityOrdered', e.target.value)} placeholder="Qty" />
              </div>
              <div className="col-span-2">
                <input className="w-full border rounded p-2" type="number" step="0.01" value={it.costPrice} onChange={(e) => updateItem(it.id, 'costPrice', e.target.value)} placeholder="Cost" />
              </div>
              <div className="col-span-1">
                <input className="w-full border rounded p-2" type="number" step="0.01" value={it.taxRate} onChange={(e) => updateItem(it.id, 'taxRate', e.target.value)} placeholder="%Tax" />
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" onClick={() => removeItem(it.id)} className="px-2 py-1 border rounded text-sm">Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Expected Delivery Date</label>
          <input type="date" className="mt-1 block w-full border rounded p-2" name="expectedDeliveryDate" value={formData.expectedDeliveryDate} onChange={handleChange} />
        </div>
        <div className="p-3 border rounded bg-white">
          <label className="text-sm font-medium">Notes</label>
          <input className="mt-1 block w-full border rounded p-2" name="notes" value={formData.notes} onChange={handleChange} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded">
          {saving ? 'Saving...' : (source ? 'Save Changes' : 'Create PO')}
        </button>
      </div>
    </form>
  );
};

export default PurchaseOrderForm;